import Keycloak from "keycloak-js";
import { cloneDeep, isNil, merge, pick } from "lodash-es";
import { getBool, parseBool, parseNumber } from "$lib/shared/utils/lodash-extensions";
import { initializeSession, updateToken, clearToken } from "$lib/stores/AuthState.svelte";

class IdentityService {
    constructor() {
        this.intervalName = "token-refresh";
        this.source = "identity.service.js";
        this.config = null;
        this.keycloak = null;
        this.TENANT_CLIENT_ID_SESSION_KEY = "tenantClientId";
    }

    get identityToken() {
        let kcValues = pick(this.keycloak, ["token", "tokenParsed"]);
        return merge({}, { token:"", tokenParsed: "" }, kcValues);
    }

    get isAuthenticated() {
        return getBool(this, "keycloak.authenticated");
    }

    async initialize({ url, clientId, realm, logoutRedirectUri }) {
        this.config = { url, clientId: sessionStorage.getItem(this.TENANT_CLIENT_ID_SESSION_KEY) ?? clientId, realm, logoutRedirectUri };
        this.keycloak = new Keycloak(this.config);

        let auth = await this.keycloak.init({
            onLoad: "check-sso",
            silentCheckSsoRedirectUri: `${window.location.origin}/silent-check-sso.html`
        });

        let redirectToLogin = !auth;

        if(redirectToLogin) {
            let redirectUrl = `${window.location.origin}${window.location.pathname}`;
            window.location.href = this.createLoginUrl(redirectUrl);
            return false;
        }

        await initializeSession(this.identityToken);
        return true;
    }

    async switchTenant(newClientId) {
        sessionStorage.setItem(this.TENANT_CLIENT_ID_SESSION_KEY, newClientId);
        await Identity.initialize({ ...Identity.config, clientId: newClientId });
    }

    async refreshToken(minValiditySecond=-1) {
        const self = this;
        // -1 forces a refresh.
        let minValidity = parseNumber(minValiditySecond, -1);

        if (isNil(self.keycloak)) {
            throw "Keycloak not initialized";
        }

        let refreshComplete = false;

        try {
            // First check if the session is still valid with silent check
            let sessionValid = await this.checkSessionValidity();
            if (!sessionValid) {
                console.log("Main SSO session is no longer valid, logging out");
                self.logOut();
                return false;
            }
            
            // If session is valid, try to refresh the token
            let refreshed = await self.keycloak.updateToken(minValidity);
            if(!parseBool(refreshed)) {
                return true; // Token was still valid, no refresh needed
            }
            updateToken(self.identityToken);
            refreshComplete = true;
        }
        catch(err) {
            self.logRefreshError(err);
            
            // If the error is related to an invalid session or token,
            // we should log the user out
            if (err && (err.message?.includes('invalid_grant') || 
                      err.error === 'invalid_token' || 
                      err.error === 'invalid_request')) {
                console.log("Token refresh failed due to invalid grant or token, logging out");
                self.logOut();
                return false;
            }
            
            // For other types of errors, just log and return false
            return false;
        }

        if(refreshComplete) {
            self.lastValidToken = cloneDeep(self.identityToken);
            return true;
        }

        return false; // Return false but don't force logout
    }

    // Check if the SSO session is still valid
    async checkSessionValidity() {
        try {
            // Check session validity using Keycloak's API
            if (this.keycloak && this.keycloak.token) {
                try {
                    // First try using Keycloak's built-in updateToken with a high validity
                    // This will fail if the refresh token is invalid/expired
                    await this.keycloak.updateToken(0); // Doesn't force refresh but checks validity
                    return true;
                } catch (tokenErr) {
                    console.log("Session validation failed via token check", tokenErr);
                    return false;
                }
            }
            
            // Fallback to iframe method if keycloak object isn't available or doesn't have a token
            const checkIframe = document.createElement('iframe');
            checkIframe.style.display = 'none';
            checkIframe.src = `${this.config.url}/realms/${this.config.realm}/protocol/openid-connect/login-status-iframe.html`;
            
            return new Promise((resolve) => {
                checkIframe.onload = () => {
                    try {
                        // If we can access this iframe, the session is likely still valid
                        document.body.removeChild(checkIframe);
                        resolve(true);
                    } catch (e) {
                        console.error("Error in iframe session check", e);
                        resolve(false);
                    }
                };
                
                checkIframe.onerror = () => {
                    try {
                        document.body.removeChild(checkIframe);
                    } catch (e) {}
                    console.log("Iframe session check failed");
                    resolve(false);
                };
                
                document.body.appendChild(checkIframe);
                
                // Set a timeout just in case
                setTimeout(() => {
                    try {
                        document.body.removeChild(checkIframe);
                    } catch (e) {}
                    // Assume invalid on timeout - this is safer
                    console.log("Iframe session check timed out");
                    resolve(false);
                }, 5000);
            });
        } catch (e) {
            console.error("Error checking session validity", e);
            // Safer to assume session is invalid on error
            return false;
        }
    }

    createLoginUrl(redirectUri=null) {
        let urlResult = this.keycloak.createLoginUrl({ redirectUri });
        return urlResult || redirectUri;
    }

    createLogoutUrl(redirectUri=null) {
        let urlResult = this.keycloak.createLogoutUrl({ redirectUri });
        return urlResult || redirectUri;
    }

    logOut(redirectUri=null) {
        sessionStorage.removeItem(this.TENANT_CLIENT_ID_SESSION_KEY)
        this.keycloak.logout({
            redirectUri: redirectUri ?? window.location.origin
        });
        clearToken();
    }

    async logRefreshError(error) {
        let message = "IdentityService.refreshToken :: Error while refreshing token";

        //TODO: incorporate server logging into log service if it's something we need to expand on
        // LogService.logError(message, error);
        console.error(message, error);

        // let {
        //     preferred_username="",
        //     rqoUserId=0,
        //     enterpriseTenantId="",
        //     publisherId=""
        // } = this.lastValidToken?.tokenParsed || {};

        // await Store.dispatch(LOG_SERVER_EVENT, new ClientLogInfo({
        //     tenantName: getTenantName(),
        //     level: LogLevel.Error,
        //     message,
        //     errorMessage: error?.message || null,
        //     userId: rqoUserId,
        //     userName: preferred_username,
        //     enterpriseTenantId,
        //     publisherId
        // }));
    }
}

export const Identity = new IdentityService();